/**
 * components/PWAPrompt.tsx
 * ------------------------------------------------------------------
 * PWA install prompt + offline indicator + update toast.
 *
 * - Android/Desktop: captures beforeinstallprompt, shows custom card
 * - iOS Safari: detects iOS + not-standalone, shows manual
 *   instructions (tap Share → Add to Home Screen)
 * - Offline bar when navigator.onLine === false
 * - Update toast when new service worker is ready
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useCallback } from 'react';
import { WifiOff, X, Share, PlusSquare } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'lkc_pwa_dismiss';
const DISMISS_DAYS = 7;

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isInStandaloneMode(): boolean {
  return ('standalone' in window.navigator && (window.navigator as unknown as { standalone: boolean }).standalone) ||
    window.matchMedia('(display-mode: standalone)').matches;
}

function wasDismissedRecently(): boolean {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  return !!dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400000;
}

export default function PWAPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosPrompt, setShowIosPrompt] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  // Android/Desktop install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      if (wasDismissedRecently()) return;
      setInstallEvent(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // iOS detection
  useEffect(() => {
    if (isIos() && !isInStandaloneMode() && !wasDismissedRecently()) {
      // Delay slightly so it doesn't flash immediately
      const t = setTimeout(() => setShowIosPrompt(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  // Online/offline
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // NOTE: the "new version available" service-worker toast that used to live
  // here was removed in the version-gate slice 2. The single, authoritative
  // update banner is now <VersionGate> (driven by appConfig/version), so there
  // is no double-banner. PWAPrompt keeps only install + offline concerns.

  const handleInstall = useCallback(async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'accepted') setShowInstall(false);
    setInstallEvent(null);
  }, [installEvent]);

  const handleDismiss = useCallback(() => {
    setShowInstall(false);
    setShowIosPrompt(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  return (
    <>
      {/* Android/Desktop install */}
      {showInstall && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0">
                <img src="/images/lkc_logo.png" alt="LKC" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 dark:text-white">Install LKC Portal</p>
                <p className="text-xs text-gray-500 mt-0.5">Add to your home screen for quick access and a native app experience.</p>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={handleInstall} className="px-4 py-2 bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white text-xs font-semibold rounded-lg shadow-sm hover:shadow-md transition-all">
                    Install
                  </button>
                  <button onClick={handleDismiss} className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">Not now</button>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-gray-300 hover:text-gray-500 shrink-0"><X size={16} /></button>
            </div>
          </div>
        </div>
      )}

      {/* iOS install instructions */}
      {showIosPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0">
                <img src="/images/lkc_logo.png" alt="LKC" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 dark:text-white">Install LKC Portal</p>
                <p className="text-xs text-gray-500 mt-1">To install this app on your iPad or iPhone:</p>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <Share size={14} className="text-primary shrink-0" />
                    <span>Tap the <strong>Share</strong> button in Safari</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <PlusSquare size={14} className="text-primary shrink-0" />
                    <span>Tap <strong>"Add to Home Screen"</strong></span>
                  </div>
                </div>
                <button onClick={handleDismiss} className="mt-3 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Got it</button>
              </div>
              <button onClick={handleDismiss} className="text-gray-300 hover:text-gray-500 shrink-0"><X size={16} /></button>
            </div>
          </div>
        </div>
      )}

      {/* Offline indicator */}
      {!online && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOff size={16} /> You're offline — some features may be unavailable
        </div>
      )}

    </>
  );
}
